/* @flow */

import { DateTime } from 'luxon';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { isString } from '@braindate/util/lib/type';

import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';
import useTimeFormat from './hook/useTimeFormat';
import messages from 'src/shared/app/base/date/l10n/dateL10n';
import {
  formatDay,
  formatWeekDay,
  formatDateTime,
  formatTime,
} from 'src/shared/app/base/date/util/dateFormatters';
import { isDateToday } from 'src/shared/app/base/date/util/dateUtils';
import LiveIcon from 'src/shared/ui/component/icon/LiveIcon';
import ClockIcon from 'src/shared/ui/component/icon/ClockIcon';
import styles from './DateTime.style';

type Props = {
  date?: DateTime | string,
  id?: string,
  withoutMonth?: boolean,
  withToday?: boolean,
  withLocalZone?: boolean,
  withIcon?: boolean,
  withAccent?: boolean,
  className?: string,
  timeText?: string | React.Node,
  isLive?: boolean,
  zone?: string,
};

const useStyles = createUseThemeStyles(styles);

/* if today && withToday = Today, Time
  if !today && withToday = Weekday date, Time => Wed 22, 10:00
  if !withToday = Weekday, Month Date, Time => Wed, April 24, 10:00
  if withoutMonth = Weekday date, Time => Wed 22, 10:00
 */
const DateTimeComp = ({
  date: propDate,
  id,
  withoutMonth,
  withToday,
  withLocalZone,
  withIcon = false,
  withAccent = false,
  className,
  timeText,
  isLive = false,
  zone,
}: Props): React.Node => {
  const classes = useStyles({ withAccent });

  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const intl = useIntl();
  const { locale } = intl;
  const timeFormat = useTimeFormat();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  let txt;
  const date: DateTime | null =
    propDate && isString(propDate)
      ? DateTime.fromISO(propDate, { zone })
      : propDate;
  if (propDate) {
    if (withToday) {
      if (isDateToday(date)) {
        txt = `${intl.formatMessage(messages.today)}, ${formatTime(
          date,
          locale,
          timeFormat,
          withLocalZone,
        )}`;
      } else if (withoutMonth) {
        txt = `${formatWeekDay(date, locale)} ${formatDay(
          date,
          locale,
        )}, ${formatTime(date, locale, timeFormat, withLocalZone)}`;
      } else {
        txt = formatDateTime(date, locale, timeFormat, withLocalZone);
      }
    } else if (withoutMonth) {
      txt = `${formatWeekDay(date, locale)} ${formatDay(
        date,
        locale,
      )}, ${formatTime(date, locale, timeFormat, withLocalZone)}`;
    } else {
      txt = formatDateTime(date, locale, timeFormat, withLocalZone);
    }
  }

  const icon = isLive ? (
    <LiveIcon className={classes.icon} />
  ) : (
    <ClockIcon className={classes.icon} />
  );

  return (
    <div className={`${className || ''} ${classes.root}`}>
      {withIcon && (
        <span className={`${classes.cell} ${classes.cellIcon}`}>{icon}</span>
      )}
      <span className={`${classes.cell}`}>
        {timeText ||
          (date && (
            <time id={id} dateTime={date.toISO()} aria-label={txt}>
              {txt}
            </time>
          ))}
      </span>
    </div>
  );
};

export default React.memo<Props>(DateTimeComp);
